import { useState, useCallback } from "react";
import { View, ScrollView, TouchableOpacity } from "react-native";
import { usePagination } from "@/hooks/usePagination";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useI18n } from "@/context/I18nContext";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { SearchInput } from "@/components/ui/SearchInput";
import { Badge } from "@/components/ui/Badge";
import { StatCard } from "@/components/ui/StatCard";
import { Table, type TableColumn } from "@/components/ui/Table";
import { TabBarNavigation } from "@/components/ui/TabBarNavigation";
import { ROLE_LABELS, ROLE_VARIANTS } from "@/constants";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import { invitationsApi, type Invitation } from "@/lib/api/invitations";
import { usersApi } from "@/lib/api/users";
import { SendInvitationModal } from "../components/SendInvitationModal";
import { ResendInviteModal } from "../components/ResendInviteModal";
import { PageFooter } from "@/components/ui/PageFooter";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { formatRelativeTime, formatSplitDateTime } from "@/lib/dateFormat";

// ─── Main Screen ─────────────────────────────────────────────────────────────

export default function InvitationsListScreen() {
  const { t, locale } = useI18n();
  const router = useRouter();
  const { top } = useSafeAreaInsets();

  const { tab: tabParam } = useLocalSearchParams<{ tab?: "all" | "resent" }>();
  const activeTab: "all" | "resent" = tabParam === "resent" ? "resent" : "all";

  const { page, setPage, limit, resetPage, search, setSearch, debouncedSearch } = usePagination({
    initialPage: 1,
    initialLimit: 10,
  });

  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [resendTarget, setResendTarget] = useState<Invitation | null>(null);

  const { data, isLoading, refetch, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ["invitations", "list", activeTab, debouncedSearch, limit],
    queryFn: ({ pageParam = 1 }) =>
      invitationsApi.list({
        tab: activeTab,
        search: debouncedSearch || undefined,
        page: pageParam as number,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.page + 1;
      return lastPage.page * lastPage.pageSize < lastPage.total ? nextPage : undefined;
    },
    enabled: true,
    placeholderData: {
      pages: [],
      pageParams: [],
    },
  });

  const handleTabChange = useCallback(
    (tab: "all" | "resent") => {
      router.setParams({ tab });
      resetPage();
    },
    [router, resetPage]
  );

  const handleSent = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleResent = useCallback(() => {
    setResendTarget(null);
    refetch();
  }, [refetch]);

  const total = data?.pages?.[0]?.total ?? 0;
  const newCount = data?.pages?.[0]?.newCount ?? 0;
  const resentCount = data?.pages?.[0]?.resentCount ?? 0;
  const items = data?.pages?.flatMap((p) => p.items) ?? [];

  const { data: stats } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => usersApi.getStats(),
  });

  const statTotalSent = stats?.invitations.totalSent ?? total;
  const statNew = stats?.invitations.new ?? newCount;
  const statResent = stats?.invitations.resent ?? resentCount;

  const columns: TableColumn<Invitation>[] = [
    {
      key: "recipient",
      header: t("staff.invitations.table.recipient"),
      flex: 1,
      renderCell: (item) => (
        <View>
          {item.recipientName ? (
            <AppText weight="medium" className="text-sm text-dark-text-50" numberOfLines={1}>
              {item.recipientName}
            </AppText>
          ) : null}
          <AppText className="text-sm text-dark-text-15" numberOfLines={1}>
            {item.recipientEmail}
          </AppText>
        </View>
      ),
    },
    {
      key: "invitedBy",
      header: t("staff.invitations.table.invitedBy"),
      width: 220,
      renderCell: (item) => (
        <View className="gap-1 items-start">
          <AppText weight="medium" className="text-sm text-dark-text-50">
            {item.invitedByName}
          </AppText>
          <Badge
            variant={ROLE_VARIANTS[item.invitedByRole]}
            label={ROLE_LABELS[item.invitedByRole]}
          />
        </View>
      ),
    },
    {
      key: "dateInvited",
      header: t("staff.invitations.table.dateInvited"),
      width: 200,
      renderCell: (item) => {
        const { date, time } = formatSplitDateTime(item.sentAt, locale);
        return (
          <>
            <AppText weight="medium" className="text-supporting-text text-base" numberOfLines={1}>
              {formatRelativeTime(item.sentAt, locale, t)}
            </AppText>
            <View className="flex-row items-center gap-1 mt-0.5">
              <AppText className="text-dark-text-20 text-sm">{date}</AppText>
              <View className="w-1 h-1 rounded-full bg-stroke-30" />
              <AppText className="text-dark-text-20 text-sm">{time}</AppText>
            </View>
          </>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: 200,
      renderCell: (item) => (
        <View className="items-end py-2">
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation?.();
              setResendTarget(item);
            }}
            accessibilityRole="button"
            accessibilityLabel={t("staff.invitations.actions.resendInvite")}
            className="flex-row items-center gap-1 px-3 py-1.5 border border-stroke-30 rounded-lg bg-white"
          >
            <Icon as={Feather} name="refresh-cw" size={13} className="text-dark-text-30" />
            <AppText className="text-sm text-dark-text-30">
              {t("staff.invitations.actions.resendInvite")}
            </AppText>
          </TouchableOpacity>
        </View>
      ),
    },
  ];

  return (
    <View className="flex-1 bg-stroke-10">
      <ScrollView
        className="flex-1 bg-white"
        testID="staff-invitations"
        contentContainerStyle={{ flexGrow: 1, paddingTop: top }}
      >
        {/* ── Page header ─────────────────────────────────────────── */}
        <View className="px-page-x tablet:px-20 pt-10 mb-10 items-start">
          <AppText weight="bold" className="text-3xl text-dark-text-50">
            {t("staff.invitations.title")}
          </AppText>
          <AppText className="text-sm text-supporting-text mt-1">
            {t("staff.invitations.subtitle")}
          </AppText>

          {/* ── Stat cards ──────────────────────────────────────── */}
          <View className="items-start w-full flex-col tablet:flex-row tablet:flex-wrap tablet:max-w-2xl gap-4 mt-6">
            <StatCard
              icon={<Icon as={Feather} name="mail" size={18} className="text-green-50" />}
              iconBgClass="bg-green-5"
              value={statTotalSent}
              title={t("staff.invitations.stats.total")}
              description={t("staff.invitations.stats.totalDesc")}
              className="w-full"
            />
            <StatCard
              icon={<Icon as={Feather} name="mail" size={18} className="text-blue-50" />}
              iconBgClass="bg-blue-5"
              value={statNew}
              title={t("staff.invitations.stats.new")}
              description={t("staff.invitations.stats.newDesc")}
              className="w-full"
            />
          </View>
        </View>

        {/* ── Table card ────────────────────────────────────────── */}
        <View className="w-full flex-col mb-10">
          <TabBarNavigation<"all" | "resent">
            tabs={[
              { key: "all", label: t("staff.invitations.tabs.all"), count: statTotalSent },
              { key: "resent", label: t("staff.invitations.tabs.resent"), count: statResent },
            ]}
            activeTab={activeTab}
            onTabPress={handleTabChange}
            contentContainerClassName="px-page-x tablet:px-20"
          />

          {/* Toolbar: Search + Send button */}
          <View className="px-page-x tablet:px-20">
            <View className="gap-3 flex-wrap flex-row tablet:items-center justify-between py-4">
              <SearchInput
                containerClassName="max-w-sm w-full mb-0"
                value={search}
                onChangeText={setSearch}
                placeholder="common.search"
              />
              <View className="items-end">
                <Button
                  icon={<Icon as={Feather} name="plus" size={14} className="text-white" />}
                  label="staff.invitations.actions.sendInvitation"
                  onPress={() => setSendModalVisible(true)}
                  variant="primary"
                />
              </View>
            </View>

            <View className="pb-6">
              <Table
                columns={columns}
                data={items}
                keyExtractor={(item) => item.id}
                minWidth={800}
                limit={data?.pages?.[0]?.pageSize ?? 10}
                totalCount={total}
                page={page}
                onPageChange={(p) => {
                  setPage(p);
                  if (p > (data?.pages?.length ?? 0) && hasNextPage) {
                    fetchNextPage();
                  }
                }}
                onRowPress={(item) => router.push(`/(staff)/invitations/${item.id}`)}
                emptyDescription="staff.invitations.empty"
                isLoading={isLoading}
              />
            </View>
          </View>
        </View>
        <PageFooter />
      </ScrollView>

      {/* ── Modals ──────────────────────────────────────────────── */}
      <SendInvitationModal
        visible={sendModalVisible}
        onDismiss={() => setSendModalVisible(false)}
        onSent={handleSent}
      />
      {resendTarget && (
        <ResendInviteModal
          visible={!!resendTarget}
          invitationId={resendTarget.id}
          email={resendTarget.recipientEmail}
          onDismiss={() => setResendTarget(null)}
          onResent={handleResent}
        />
      )}
    </View>
  );
}
